import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { Settings } from 'lucide-react';
import { apiService } from './services/api';
import Swal from 'sweetalert2';
import LoginPage from './components/LoginPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedComponent from './components/RoleBasedComponent';
import { useResponsive } from './hooks/usePWA';
import { useTheme } from './hooks/useTheme';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import GlobalSearch from './components/GlobalSearch';
import ToastNotifications from './components/ToastNotifications';
import AnnouncementPopup from './components/AnnouncementPopup';
import NotificationSettingsPage from './components/NotificationSettings';
import CallNotification from './components/CallNotification';
import VideoCallModal from './components/VideoCallModal';
import { useNotifications, setGlobalNotificationHook } from './hooks/useNotifications';
import { useMessaging, setGlobalMessagingHook } from './hooks/useMessaging';
import LoginNotifications from './components/LoginNotifications';
import Dashboard from './components/Dashboard';
import MobileDashboard from './components/MobileDashboard';
import MobileNavigation from './components/MobileNavigation';
import PWAInstallBanner from './components/PWAInstallBanner';
import OfflineIndicator from './components/OfflineIndicator';
import UpdateNotification from './components/UpdateNotification';
import ErrorBoundary from './components/ErrorBoundary';
import LocationsPage from './components/LocationsPage';
import CompaniesPage from './components/CompaniesPage';
import GSMLinesPage from './components/GSMLinesPage';
import TariffsPage from './components/TariffsPage';
import CapexPage from './components/CapexPage';
import MeetingsPage from './components/MeetingsPage';
import OrdersPage from './components/OrdersPage';
import SuppliersPage from './components/SuppliersPage';
import TechnicalServicePage from './components/TechnicalServicePage';
import UsersPage from './components/UsersPage';
import ProfilePage from './components/ProfilePage';
import UserActivityPage from './components/UserActivityPage';
import RBACManagement from './components/RBACManagement';
import PhysicalServersPage from './components/PhysicalServersPage';
import VirtualServersPage from './components/VirtualServersPage';
import NetworkSwitchesPage from './components/NetworkSwitchesPage';
import SwitchConfigWizard from './components/SwitchConfigWizard';
import IoTDevicesPage from './components/IoTDevicesPage';
import RadiosPage from './components/RadiosPage';
import RadioDetailsPage from './pages/RadioDetailsPage';
import SmartScalesPage from './components/SmartScalesPage';
import ScaleDetailsPage from './pages/ScaleDetailsPage';
import StorageUnitsPage from './components/StorageUnitsPage';
import CargoLabelsPage from './components/CargoLabelsPage';
import DamageTheftPage from './components/DamageTheftPage';
import CabinetsPage from './components/CabinetsPage';
import MobilePhonesPage from './components/MobilePhonesPage';
import BrandModelManagement from './components/BrandModelManagement';
import ComputersPage from './components/ComputersPage';
import MonitorsPage from './components/MonitorsPage';
import AssignmentFormsPage from './components/AssignmentFormsPage';
import AssignmentFormPDF from './components/AssignmentFormPDF';
import AssignmentDefinitionsPage from './components/AssignmentDefinitionsPage';
import SystemSettingsPage from './components/SystemSettingsPage';
import MenuManagementPage from './components/MenuManagementPage';
import AssignmentApprovalPage from './components/AssignmentApprovalPage';
import SupplierLoginPage from './components/SupplierLoginPage';
import SupplierDashboard from './components/SupplierDashboard';
import SupplierCameraFaultsPage from './pages/SupplierCameraFaultsPage';
import SupplierResetPasswordPage from './components/SupplierResetPasswordPage';
import ServiceQuery from './components/ServiceQuery';
import EquipmentPage from './components/EquipmentPage';
import ConsumablesPage from './components/ConsumablesPage';
import AssignmentFormPage from './components/AssignmentFormPage';
import AssignmentLogsPage from './components/AssignmentLogsPage';
import AnnouncementsPage from './components/AnnouncementsPage';
import PrintersPage from './components/PrintersPage';
import CircuitsPage from './components/CircuitsPage';
import HandheldTerminalsPage from './components/HandheldTerminalsPage';
import DocumentsPage from './components/DocumentsPage';
import MaintenanceContractsPage from './components/MaintenanceContractsPage';
import NvrsPage from './components/NvrsPage';
import CamerasPage from './components/CamerasPage';
import CameraFaultsPage from './components/CameraFaultsPage';
import PhoneReplacementPage from './components/PhoneReplacementPage';
import MailDiagnosticsPage from './pages/MailDiagnosticsPage';
import ComputerModelsPage from './pages/ComputerModelsPage';
import ComputerBrandsPage from './pages/ComputerBrandsPage';
import ScrapComputersPage from './pages/ScrapComputersPage';
import ProceduresPage from './components/ProceduresPage';
import SecurityVulnerabilitiesPage from './components/SecurityVulnerabilitiesPage';
import ChecklistPage from './components/ChecklistPage';
import ChecklistPopup from './components/ChecklistPopup';
import NotepadPage from './components/NotepadPage';

import OnlineUsersWidget from './components/OnlineUsersWidget';
import { useRealtimeEvents } from './hooks/useRealtimeEvents';
import { useGroupChatSound } from './hooks/useGroupChatSound';
import { soundManager } from './utils/soundManager';

const AppContent: React.FC = () => {
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    // URL'den sayfa adını al
    const path = window.location.pathname.replace('/', '') || 'dashboard';
    return path;
  });
  const { isMobile } = useResponsive();
  const notificationHook = useNotifications();
  const messagingHook = useMessaging();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [systemLogo, setSystemLogo] = useState<string | null>(null);
  const [systemTitle, setSystemTitle] = useState('IT Toolbox');
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [callRoomName, setCallRoomName] = useState('');
  const [activeCallId, setActiveCallId] = useState<number | null>(null);
  const [callType, setCallType] = useState<'audio' | 'video'>('video');

  // Debug user
  // console.log('App user:', user);

  // Initialize realtime events for all users
  // Only connect when user is authenticated and has a role
  useRealtimeEvents(user?.role ?? '');
  useGroupChatSound();

  // Set global notification hook for use across the app
  React.useEffect(() => {
    setGlobalNotificationHook(notificationHook);
    setGlobalMessagingHook(messagingHook);
    
    // Initialize sound system on first user interaction
    const initializeSound = () => {
      soundManager.initialize();
    };
    
    // Listen for first user interaction
    ['click', 'touchstart', 'keydown'].forEach(event => {
      document.addEventListener(event, initializeSound, { once: true });
    });
    
    return () => {
      ['click', 'touchstart', 'keydown'].forEach(event => {
        document.removeEventListener(event, initializeSound);
      });
    };
  }, [notificationHook]);

  // Login sonrası okunmamış mesaj kontrolü
  React.useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (!['admin', 'superadmin', 'teknisyen'].includes(user.role)) return;

    const justLoggedIn = sessionStorage.getItem('justLoggedIn');
    if (!justLoggedIn) return;
    sessionStorage.removeItem('justLoggedIn');

    const checkUnread = async () => {
      try {
        const unreadData = await apiService.getUnreadMessageCounts();
        const total = Object.values(unreadData as Record<string, number>).reduce((sum, c) => sum + c, 0);
        if (total > 0) {
          const result = await Swal.fire({
            title: 'Okunmamış Mesajlar',
            text: `${total} okunmamış mesajınız var. Okumak ister misiniz?`,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Evet, göster',
            cancelButtonText: 'Şimdi değil',
            confirmButtonColor: '#3b82f6',
            cancelButtonColor: '#6b7280',
          });
          if (result.isConfirmed) {
            window.dispatchEvent(new CustomEvent('openChatWidget'));
          }
        }
      } catch (error) {
        console.error('Error checking unread messages on login:', error);
      }
    };

    const timer = setTimeout(checkUnread, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user]);

  // Load system settings for loading spinner
  React.useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        const settings = await apiService.getPublicSystemSettings();
        if (Array.isArray(settings)) {
          // Logo ayarını bul
          const logoSetting = settings.find((s: any) => s.setting_key === 'system_logo');
          if (logoSetting && logoSetting.setting_value) {
            const timestamp = new Date().getTime();
            setSystemLogo(`${logoSetting.setting_value}?t=${timestamp}`);
          }
          
          // Title ayarını bul
          const titleSetting = settings.find((s: any) => s.setting_key === 'system_title');
          if (titleSetting && titleSetting.setting_value) {
            setSystemTitle(titleSetting.setting_value);
          }
        }
      } catch (error) {
        console.error('Error loading system settings:', error);
        // Hata durumunda varsayılan değerleri kullan
        setSystemTitle('IT Toolbox');
      }
    };

    loadSystemSettings();

    // Sistem ayarları güncelleme event listener'ı
    const handleSystemSettingsUpdate = () => {
      loadSystemSettings();
    };

    window.addEventListener('systemSettingsUpdated', handleSystemSettingsUpdate);
    
    return () => {
      window.removeEventListener('systemSettingsUpdated', handleSystemSettingsUpdate);
    };
  }, []);

  React.useEffect(() => {
    const handleNavigate = (event: any) => {
      const page = event.detail;
      setCurrentPage(page);
      // URL'i güncelle
      window.history.pushState(null, '', `/${page}`);
      setSidebarOpen(false); // Close sidebar on mobile after navigation
    };

    // Sayfa yüklendiğinde URL'den currentPage'i ayarla
    const path = window.location.pathname.replace('/', '') || 'dashboard';
    setCurrentPage(path);

    // Browser back/forward butonları için
    const handlePopState = () => {
      const path = window.location.pathname.replace('/', '') || 'dashboard';
      setCurrentPage(path);
    };

    window.addEventListener('navigate', handleNavigate);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('navigate', handleNavigate);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-sm w-full">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse shadow-lg shadow-blue-500/30">
            {systemLogo ? (
              <img
                src={systemLogo}
                alt="Logo"
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <Settings className={`w-6 h-6 sm:w-8 sm:h-8 text-white ${systemLogo ? 'hidden' : ''}`} />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">{systemTitle}</h2>
          <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">Yükleniyor...</p>
          <div className="mt-4">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    // Hedef URL'yi kaydet — login sonrası geri dönmek için
    const intended = window.location.pathname + window.location.search;
    const isPublicPage = intended === '/' || intended.startsWith('/login') ||
      intended.startsWith('/supplier') || intended.startsWith('/reset-password') ||
      intended.startsWith('/zimmet-onayla');
    if (!isPublicPage && intended !== '/') {
      sessionStorage.setItem('redirectAfterLogin', intended);
    }
    return <LoginPage />;
  }


  const renderContent = () => {
    try {
      // Dinamik route handling için
      if (currentPage.startsWith('assignment-form-pdf/')) {
        return (
          <ProtectedRoute menuName="mobile_phones">
            <AssignmentFormPDF />
          </ProtectedRoute>
        );
      }

      // Assignment form view route
      if (currentPage.startsWith('assigment-form/')) {
        return (
          <ProtectedRoute menuName="mobile_phones">
            <AssignmentFormPDF />
          </ProtectedRoute>
        );
      }
      
      switch (currentPage) {
        case 'dashboard':
          return isMobile ? (
            <MobileDashboard
              onNavigate={setCurrentPage}
              onStartVideoCall={(userId, callType, callId, roomName) => {
                setCallRoomName(roomName);
                setActiveCallId(callId);
                setCallType(callType);
                setShowVideoCall(true);
              }}
            />
          ) : (
            <Dashboard />
          );
        case 'locations':
        return (
          <ProtectedRoute resource="locations" action="view">
            <LocationsPage />
          </ProtectedRoute>
        );
      case 'companies':
        return (
          <ProtectedRoute resource="companies" action="view">
            <CompaniesPage />
          </ProtectedRoute>
        );
      case 'suppliers':
        return (
          <ProtectedRoute resource="suppliers" action="view">
            <SuppliersPage />
          </ProtectedRoute>
        );
      case 'gsm-lines':
        return (
          <ProtectedRoute menuName="gsm_lines_main">
            <GSMLinesPage />
          </ProtectedRoute>
        );
      case 'tariffs':
        return (
          <ProtectedRoute menuName="tariffs">
            <TariffsPage />
          </ProtectedRoute>
        );
      case 'capex':
        return (
          <ProtectedRoute resource="capex" action="view">
            <CapexPage />
          </ProtectedRoute>
        );
      case 'meetings':
        return (
          <ProtectedRoute menuName="meetings">
            <MeetingsPage />
          </ProtectedRoute>
        );
      case 'orders':
        return (
          <ProtectedRoute resource="orders" action="view">
            <OrdersPage />
          </ProtectedRoute>
        );
      case 'technical-service':
        return (
          <ProtectedRoute resource="technical-service" action="view">
            <TechnicalServicePage />
          </ProtectedRoute>
        );
      case 'users':
        return (
          <ProtectedRoute resource="users" action="view">
            <UsersPage />
          </ProtectedRoute>
        );
      case 'profile':
        return (
          <ProtectedRoute menuName="profile">
            <ProfilePage />
          </ProtectedRoute>
        );
      case 'user-activity':
        return (
          <ProtectedRoute menuName="user-activity">
            <UserActivityPage />
          </ProtectedRoute>
        );
      case 'physical-servers':
        return (
          <ProtectedRoute menuName="physical_servers">
            <PhysicalServersPage />
          </ProtectedRoute>
        );
      case 'virtual-servers':
        return (
          <ProtectedRoute menuName="virtual_servers">
            <VirtualServersPage />
          </ProtectedRoute>
        );
      case 'switches':
        return (
          <ProtectedRoute resource="network-switches" action="view">
            <NetworkSwitchesPage />
          </ProtectedRoute>
        );
      case 'switch-config-wizard':
        return (
          <ProtectedRoute resource="network-switches" action="view">
            <SwitchConfigWizard />
          </ProtectedRoute>
        );
      case 'iot-devices':
        return (
          <ProtectedRoute resource="iot-devices" action="view">
            <IoTDevicesPage />
          </ProtectedRoute>
        );
      case 'handheld-terminals':
        return (
          <ProtectedRoute menuName="handheld_terminals">
            <HandheldTerminalsPage />
          </ProtectedRoute>
        );
      case 'radios':
        return (
          <ProtectedRoute menuName="radios">
            <RadiosPage />
          </ProtectedRoute>
        );
      case 'scales':
        return (
          <ProtectedRoute menuName="scales">
            <SmartScalesPage />
          </ProtectedRoute>
        );
      case 'storage-units':
        return (
          <ProtectedRoute menuName="storage">
            <StorageUnitsPage />
          </ProtectedRoute>
        );
      case 'cargo-labels':
        return (
          <ProtectedRoute resource="orders" action="view">
            <CargoLabelsPage />
          </ProtectedRoute>
        );
      case 'damage-theft':
        return (
          <ProtectedRoute resource="orders" action="view">
            <DamageTheftPage />
          </ProtectedRoute>
        );
      case 'cabinets':
        return (
          <ProtectedRoute menuName="cabinets">
            <CabinetsPage />
          </ProtectedRoute>
        );
      case 'mobile-phones':
        return (
          <ProtectedRoute menuName="mobile_phones">
            <MobilePhonesPage />
          </ProtectedRoute>
        );
      case 'brand-model-management':
        return (
          <ProtectedRoute menuName="mobile_phones">
            <BrandModelManagement />
          </ProtectedRoute>
        );
      case 'computers':
        return (
          <ProtectedRoute menuName="computers">
            <ComputersPage />
          </ProtectedRoute>
        );
      case 'computer-models':
        return (
          <ProtectedRoute menuName="computers">
            <ComputerModelsPage />
          </ProtectedRoute>
        );
      case 'computer-brands':
        return (
          <ProtectedRoute menuName="computers">
            <ComputerBrandsPage />
          </ProtectedRoute>
        );
      case 'scrap-computers':
        return (
          <ProtectedRoute menuName="computers">
            <ScrapComputersPage />
          </ProtectedRoute>
        );
      case 'monitors':
        return (
          <ProtectedRoute menuName="monitors">
            <MonitorsPage />
          </ProtectedRoute>
        );
      case 'assignment-forms':
        return (
          <ProtectedRoute menuName="mobile_phones">
            <AssignmentFormsPage />
          </ProtectedRoute>
        );
      case 'assignment-definitions':
        return (
          <ProtectedRoute menuName="assignment-definitions">
            <AssignmentDefinitionsPage />
          </ProtectedRoute>
        );
      case 'assignment-form-pdf':
        return (
          <ProtectedRoute menuName="mobile_phones">
            <AssignmentFormPDF />
          </ProtectedRoute>
        );
      case 'notification-settings':
        return (
          <ProtectedRoute menuName="notification-settings">
            <NotificationSettingsPage />
          </ProtectedRoute>
        );
      case 'system-settings':
        return (
          <ProtectedRoute menuName="system-settings">
            <SystemSettingsPage />
          </ProtectedRoute>
        );
      case 'menu-management':
        return (
          <ProtectedRoute menuName="menu-management">
            <MenuManagementPage />
          </ProtectedRoute>
        );
      case 'rbac-management':
        return (
          <ProtectedRoute resource="role-permissions" action="manage">
            <RBACManagement />
          </ProtectedRoute>
        );
      case 'equipment':
        return (
          <ProtectedRoute menuName="equipment">
            <EquipmentPage />
          </ProtectedRoute>
        );
      case 'consumables':
        return (
          <ProtectedRoute menuName="consumables">
            <ConsumablesPage />
          </ProtectedRoute>
        );
      case 'equipment/assignments':
      case 'consumables/assignments':
        return (
          <ProtectedRoute menuName="equipment">
            <AssignmentFormPage />
          </ProtectedRoute>
        );
      case 'assignment-logs':
        return (
          <ProtectedRoute menuName="assignment-logs">
            <AssignmentLogsPage />
          </ProtectedRoute>
        );
      case 'announcements':
        return (
          <ProtectedRoute menuName="announcements">
            <AnnouncementsPage />
          </ProtectedRoute>
        );
      case 'printers':
        return (
          <ProtectedRoute menuName="printers">
            <PrintersPage />
          </ProtectedRoute>
        );
      case 'circuits':
        return (
          <ProtectedRoute menuName="circuits">
            <CircuitsPage />
          </ProtectedRoute>
        );
      case 'documents':
        return (
          <ProtectedRoute menuName="documents">
            <DocumentsPage />
          </ProtectedRoute>
        );
      case 'maintenance-contracts':
        return (
          <ProtectedRoute menuName="maintenance-contracts">
            <MaintenanceContractsPage />
          </ProtectedRoute>
        );
      case 'nvrs':
        return (
          <ProtectedRoute menuName="nvrs">
            <NvrsPage />
          </ProtectedRoute>
        );
      case 'cameras':
        return (
          <ProtectedRoute menuName="cameras">
            <CamerasPage />
          </ProtectedRoute>
        );
      case 'camera-faults':
        return (
          <ProtectedRoute menuName="camera-faults">
            <CameraFaultsPage />
          </ProtectedRoute>
        );
      case 'phone-replacements':
        return (
          <ProtectedRoute menuName="phone-replacements">
            <PhoneReplacementPage />
          </ProtectedRoute>
        );
      case 'mail-diagnostics':
        return (
          <ProtectedRoute menuName="mail-diagnostics">
            <MailDiagnosticsPage />
          </ProtectedRoute>
        );
      case 'procedures':
        return (
          <ProtectedRoute menuName="procedures">
            <ProceduresPage />
          </ProtectedRoute>
        );
      case 'checklists':
        return (
          <ProtectedRoute requiredRoles={['superadmin', 'admin']}>
            <ChecklistPage />
          </ProtectedRoute>
        );
      case 'security-vulnerabilities':
        return (
          <ProtectedRoute requiredRoles={['superadmin', 'admin']}>
            <SecurityVulnerabilitiesPage />
          </ProtectedRoute>
        );
      case 'notepad':
        return (
          <ProtectedRoute requiredRoles={['superadmin', 'admin']}>
            <NotepadPage />
          </ProtectedRoute>
        );
      default:
          return isMobile ? (
            <MobileDashboard
              onNavigate={setCurrentPage}
              onStartVideoCall={(userId, callType, callId, roomName) => {
                setCallRoomName(roomName);
                setActiveCallId(callId);
                setCallType(callType);
                setShowVideoCall(true);
              }}
            />
          ) : (
            <Dashboard />
          );
      }
    } catch (error) {
      console.error('Error rendering content:', error);
      return (
        <div className="p-6 text-center">
          <div className="text-red-600 mb-4">Sayfa yüklenirken hata oluştu</div>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Sayfayı Yenile
          </button>
        </div>
      );
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      {/* PWA Install Banner */}
      <PWAInstallBanner />
      
      {/* Offline Indicator */}
      <OfflineIndicator />
      
      {/* Login Notifications */}
      <LoginNotifications />

      {/* Call Notification */}
      <CallNotification
        onAccept={(call) => {
          setCallRoomName(call.roomName);
          setActiveCallId(call.callId);
          setCallType(call.callType as 'audio' | 'video');
          setShowVideoCall(true);
        }}
        onReject={(callId) => {
          console.log('Call rejected:', callId);
        }}
      />

      {/* Video Call Modal */}
      {showVideoCall && callRoomName && (
        <VideoCallModal
          isOpen={showVideoCall}
          onClose={() => {
            setShowVideoCall(false);
            setCallRoomName('');
            setActiveCallId(null);
          }}
          roomName={callRoomName}
          callId={activeCallId || undefined}
          participantName={user?.name}
          callType={callType}
        />
      )}

      {/* Desktop Sidebar */}
      {!isMobile && <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />}
      
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Desktop Header */}
        {!isMobile && <Header onMenuToggle={toggleSidebar} onNavigate={setCurrentPage} />}
        
        {/* Mobile Header */}
        {isMobile && (
          <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800/80 px-4 py-2 min-h-[56px] flex items-center justify-between sticky top-0 z-40 transition-colors">
            <h1 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate flex-1 mr-2">
              {currentPage === 'dashboard' ? systemTitle :
               currentPage === 'orders' ? t('menu.orders') :
               currentPage === 'technical-service' ? t('menu.technical_service') :
               currentPage === 'damage-theft' ? t('menu.damage_theft') :
               currentPage === 'capex' ? t('menu.capex') :
               currentPage === 'meetings' ? t('menu.meetings') :
               currentPage === 'users' ? t('menu.users') :
               currentPage === 'gsm-lines' ? t('menu.gsm_lines') :
               currentPage === 'tariffs' ? t('menu.tariffs') :
               currentPage === 'companies' ? t('menu.companies') :
               currentPage === 'locations' ? t('menu.locations') :
               currentPage === 'suppliers' ? t('menu.suppliers') :
               currentPage === 'physical-servers' ? t('menu.physical_servers') :
               currentPage === 'virtual-servers' ? t('menu.virtual_servers') :
               currentPage === 'iot-devices' ? t('menu.iot') :
               currentPage === 'scales' ? t('menu.scales') :
               currentPage === 'storage-units' ? t('menu.storage') :
               currentPage === 'switches' ? t('menu.network_switches') :
               currentPage === 'switch-config-wizard' ? 'Switch Config Wizard' :
               currentPage === 'cabinets' ? t('menu.cabinets') :
               currentPage === 'cargo-labels' ? t('menu.cargo_labels') :
               currentPage === 'user-activity' ? t('usermenu.user_activities') :
               currentPage === 'system-settings' ? t('menu.system_settings') :
               currentPage === 'equipment' ? t('menu.equipment') :
               currentPage === 'consumables' ? t('menu.consumables') :
               currentPage === 'printers' ? t('menu.printers') :
               currentPage === 'camera-faults' ? t('menu.camera-faults') :
               currentPage === 'profile' ? t('menu.profile') :
               currentPage === 'rbac-management' ? t('menu.rbac') :
               currentPage === 'announcements' ? t('menu.announcements') :
               currentPage === 'procedures' ? t('menu.procedures') :
               currentPage === 'checklists' ? t('menu.checklists') :
               currentPage === 'notepad' ? t('menu.notepad') :
               currentPage === 'security-vulnerabilities' ? t('menu.security_vulnerabilities') :
               systemTitle}
            </h1>
            <GlobalSearch onNavigate={setCurrentPage} />
          </div>
        )}
        
        <main className={`flex-1 overflow-y-auto ${isMobile ? 'px-0 pb-20' : ''}`}>
          {renderContent()}
        </main>
        
        {/* Desktop Footer */}
        {!isMobile && (
          <footer className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-t border-slate-200 dark:border-slate-800/80">
            {/* Version Update Notification in Footer */}
            <UpdateNotification variant="footer" />
            <div className="p-4">
              <div className="text-center text-sm text-slate-400 dark:text-slate-500">
                {t('login.copyright')}
              </div>
            </div>
          </footer>
        )}

        {/* Mobile Footer with spacing for bottom navigation */}
        {isMobile && (
          <div className="bg-slate-50 dark:bg-slate-900">
            {/* Version Update Notification in Mobile Footer */}
            <UpdateNotification variant="footer" />
            <div className="h-4"></div>
          </div>
        )}
      </div>
      
      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <MobileNavigation 
          currentPage={currentPage} 
          onNavigate={setCurrentPage} 
        />
      )}
      
      {/* Toast Notifications */}
      <ToastNotifications
        notifications={notificationHook.toasts}
        onDismiss={notificationHook.removeToast}
      />

      {/* Announcement Popup */}
      <AnnouncementPopup />

      {/* Checklist Popup */}
      <ChecklistPopup />

      {/* Online Users Widget */}
      <OnlineUsersWidget
        onStartVideoCall={(userId, callType, callId, roomName) => {
          setCallRoomName(roomName);
          setActiveCallId(callId);
          setCallType(callType);
          setShowVideoCall(true);
        }}
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LanguageProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/zimmet-onayla" element={<AssignmentApprovalPage />} />
            <Route path="/supplier-login" element={<SupplierLoginPage />} />
            <Route path="/supplier-reset-password" element={<SupplierResetPasswordPage />} />
            <Route path="/supplier-dashboard" element={<SupplierDashboard />} />
            <Route path="/supplier-camera-faults" element={<SupplierCameraFaultsPage />} />
            <Route path="/scale-details/:id" element={<ScaleDetailsPage />} />
            <Route path="/radio-details/:id" element={<RadioDetailsPage />} />
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;